import { readPref, writePref } from "@/lib/useLocalStorage";

/**
 * Full column catalogue for the Ads Manager, mirroring the design
 * bundle's Edit-columns modal. Columns are keyed by short IDs used
 * throughout StatsTable and MetricCells. Adding a new column is a
 * two-step: (1) add the entry here, (2) add a case to MetricCells.
 */

export type ColumnKey =
  // Config / parameters
  | "status"
  | "label"
  | "ad_group_id"
  | "keyword_id"
  | "match_type"
  | "translation_en"
  | "daily_budget"
  | "default_bid"
  | "cpa_goal"
  | "bid"
  | "campaign_id"
  | "placement"
  | "country"
  | "target_cpa"
  | "rules"
  | "tracked_installs"
  // last_modified_at / modified_by — "Last Date Modified" + "Modified
  // By" Ads-Manager columns (since 0062). Stamped only on successful
  // ASA push from our drainer; NULL for entities never edited through
  // the platform. modified_by ∈ 'manual' | 'by_rule' | 'by_autopilot'.
  | "last_modified_at"
  | "modified_by"
  // creation_time — "Created Date": ASA's own entity creationTime
  // (immutable). Available on all 3 grains (confirmed against the live
  // ASA API — ad_group carries it too, despite the docs). Distinct from
  // last_modified_at (platform-edit stamp).
  | "creation_time"
  // rule_applied / rule_names — surface rules attached to this entity
  // OR (per grain) its ancestors / descendants. rule_applied = green
  // dot when any rule attached. rule_names = comma-separated list
  // with hover tooltip for the full set.
  | "rule_applied"
  | "rule_names"
  | "qs_status"
  | "goal_based_opt"
  | "active_cpp"
  | "tags"
  // Ad-group ASA targeting parameters (data not yet wired)
  | "search_match"
  | "devices"
  | "gender"
  | "age"
  | "locality"
  | "admin_area"
  // Performance
  | "impressions"
  | "ttr"
  | "taps"
  | "avg_cpt"
  | "spend"
  | "installs"
  | "installs_tap"
  | "installs_view"
  | "installs_total"
  | "cpa"
  | "cpa_tap"
  | "cpa_view"
  | "cpa_total"
  | "cr_tap"
  | "cr_total"
  | "new_downloads_tap"
  | "new_downloads_view"
  | "new_downloads_total"
  | "redownloads_tap"
  | "redownloads_view"
  | "redownloads_total"
  | "impression_share"
  | "sov_slot2"
  | "sov_total"
  | "search_rank"
  | "sov_2d"
  | "sov_5d"
  // Enriched data
  | "ipm"
  | "cpm"
  | "active_capacity"
  | "active_keywords"
  | "active_share"
  | "search_ads_popularity"
  | "organic_search"
  | "organic_position"
  // Conversions — per-Adapty-event breakdown (18 native event_types ×
  // {count, _cr, _revenue, _cpa} = 72 columns) plus a single
  // `revenue` aggregate (sum of all classified events' revenue, signed —
  // refunds subtract). Legacy `trials/trial_cvr/paid_cvr` were retired
  // on 2026-04-29; `revenue` came back on 2026-04-30 because users
  // wanted one "total revenue" cell instead of mentally summing 18
  // per-event_revenue cells.
  | "revenue"
  | "trial_started"
  | "trial_started_cr"
  | "trial_started_revenue"
  | "trial_started_cpa"
  | "trial_converted"
  | "trial_converted_cr"
  | "trial_converted_revenue"
  | "trial_converted_cpa"
  | "trial_renewal_cancelled"
  | "trial_renewal_cancelled_cr"
  | "trial_renewal_cancelled_revenue"
  | "trial_renewal_cancelled_cpa"
  | "trial_renewal_reactivated"
  | "trial_renewal_reactivated_cr"
  | "trial_renewal_reactivated_revenue"
  | "trial_renewal_reactivated_cpa"
  | "trial_expired"
  | "trial_expired_cr"
  | "trial_expired_revenue"
  | "trial_expired_cpa"
  | "subscription_started"
  | "subscription_started_cr"
  | "subscription_started_revenue"
  | "subscription_started_cpa"
  | "subscription_renewed"
  | "subscription_renewed_cr"
  | "subscription_renewed_revenue"
  | "subscription_renewed_cpa"
  | "subscription_renewal_cancelled"
  | "subscription_renewal_cancelled_cr"
  | "subscription_renewal_cancelled_revenue"
  | "subscription_renewal_cancelled_cpa"
  | "subscription_renewal_reactivated"
  | "subscription_renewal_reactivated_cr"
  | "subscription_renewal_reactivated_revenue"
  | "subscription_renewal_reactivated_cpa"
  | "subscription_expired"
  | "subscription_expired_cr"
  | "subscription_expired_revenue"
  | "subscription_expired_cpa"
  | "subscription_paused"
  | "subscription_paused_cr"
  | "subscription_paused_revenue"
  | "subscription_paused_cpa"
  | "subscription_deferred"
  | "subscription_deferred_cr"
  | "subscription_deferred_revenue"
  | "subscription_deferred_cpa"
  | "subscription_refunded"
  | "subscription_refunded_cr"
  | "subscription_refunded_revenue"
  | "subscription_refunded_cpa"
  | "non_subscription_purchase"
  | "non_subscription_purchase_cr"
  | "non_subscription_purchase_revenue"
  | "non_subscription_purchase_cpa"
  | "non_subscription_purchase_refunded"
  | "non_subscription_purchase_refunded_cr"
  | "non_subscription_purchase_refunded_revenue"
  | "non_subscription_purchase_refunded_cpa"
  | "entered_grace_period"
  | "entered_grace_period_cr"
  | "entered_grace_period_revenue"
  | "entered_grace_period_cpa"
  | "billing_issue_detected"
  | "billing_issue_detected_cr"
  | "billing_issue_detected_revenue"
  | "billing_issue_detected_cpa"
  | "access_level_updated"
  | "access_level_updated_cr"
  | "access_level_updated_revenue"
  | "access_level_updated_cpa"
  // Adjust per-event breakdown (10 events × count/cr/cpa, +revenue for
  // purchase-like events). Populated only when the app's cohort source
  // = 'adjust'; read from row.adjust. See Stage 3 of the Adjust→GCS
  // integration.
  | "adj_mobilepurchase"
  | "adj_mobilepurchase_cr"
  | "adj_mobilepurchase_cpa"
  | "adj_mobilepurchase_revenue"
  | "adj_rv_nonsubscriptionpurchase"
  | "adj_rv_nonsubscriptionpurchase_cr"
  | "adj_rv_nonsubscriptionpurchase_cpa"
  | "adj_rv_nonsubscriptionpurchase_revenue"
  | "adj_rv_productchange"
  | "adj_rv_productchange_cr"
  | "adj_rv_productchange_cpa"
  | "adj_rv_subscriptioncancel"
  | "adj_rv_subscriptioncancel_cr"
  | "adj_rv_subscriptioncancel_cpa"
  | "adj_rv_subscriptionexpiration"
  | "adj_rv_subscriptionexpiration_cr"
  | "adj_rv_subscriptionexpiration_cpa"
  | "adj_rv_subscriptioninitialpurchase"
  | "adj_rv_subscriptioninitialpurchase_cr"
  | "adj_rv_subscriptioninitialpurchase_cpa"
  | "adj_rv_subscriptioninitialpurchase_revenue"
  | "adj_rv_subscriptionrenewal"
  | "adj_rv_subscriptionrenewal_cr"
  | "adj_rv_subscriptionrenewal_cpa"
  | "adj_rv_subscriptionrenewal_revenue"
  | "adj_rv_subscriptiontrialactivate"
  | "adj_rv_subscriptiontrialactivate_cr"
  | "adj_rv_subscriptiontrialactivate_cpa"
  | "adj_rv_subscriptiontrialactivate_revenue"
  | "adj_rv_subscriptiontrialcancel"
  | "adj_rv_subscriptiontrialcancel_cr"
  | "adj_rv_subscriptiontrialcancel_cpa"
  | "adj_rv_subscriptiontrialstart"
  | "adj_rv_subscriptiontrialstart_cr"
  | "adj_rv_subscriptiontrialstart_cpa"
  // Cohort — revenue / ROAS / ROI / ARPU × D1 / D4 / D7 / D10 / D20 / D30 / D35
  | "roas"
  | "roas_d1"
  | "roas_d4"
  | "roas_d7"
  | "roas_d10"
  | "roas_d20"
  | "roas_d30"
  | "roas_d35"
  | "roi_d1"
  | "roi_d4"
  | "roi_d7"
  | "roi_d10"
  | "roi_d20"
  | "roi_d30"
  | "roi_d35"
  | "arpu_d1"
  | "arpu_d4"
  | "arpu_d7"
  | "arpu_d10"
  | "arpu_d20"
  | "arpu_d30"
  | "arpu_d35";

export type ColumnCategory =
  | "parameters"
  | "performance"
  | "enriched"
  | "conversions"
  | "cohort";

export type ColumnDef = {
  key: ColumnKey;
  label: string;
  category: ColumnCategory;
  defaultVisible: boolean;
  /** True when the underlying data isn't in the DB yet. Column is
   *  shown in the modal so users can opt in, but the cell renders
   *  "—" until the backend piece lands. */
  comingSoon?: boolean;
};

export const ALL_COLUMNS: ColumnDef[] = [
  // Parameters
  // First in the catalogue so it lands directly after the pinned keys, i.e. as
  // close to the keyword text as an unpinned column can sit. Pinning it would
  // put it flush against the identity column but would also make it
  // permanently un-hideable, and a reference attribute the user cannot switch
  // off is worse than one column of distance.
  { key: "translation_en",   label: "Translation",          category: "parameters", defaultVisible: true  },
  { key: "status",           label: "Status",               category: "parameters", defaultVisible: true  },
  { key: "label",            label: "Label",                category: "parameters", defaultVisible: false },
  { key: "ad_group_id",      label: "Ad group ID",          category: "parameters", defaultVisible: false },
  { key: "campaign_id",      label: "Campaign ID",          category: "parameters", defaultVisible: false },
  { key: "placement",        label: "Placement",            category: "parameters", defaultVisible: false },
  { key: "country",          label: "Country or Region",    category: "parameters", defaultVisible: false },
  { key: "daily_budget",     label: "Daily budget",         category: "parameters", defaultVisible: true  },
  { key: "default_bid",      label: "Default max CPT bid",  category: "parameters", defaultVisible: true  },
  { key: "cpa_goal",         label: "CPA goal",             category: "parameters", defaultVisible: true  },
  { key: "target_cpa",       label: "Target CPA",           category: "parameters", defaultVisible: false },
  { key: "bid",              label: "CPT bid",              category: "parameters", defaultVisible: true  },
  { key: "keyword_id",       label: "Keyword ID",           category: "parameters", defaultVisible: false },
  { key: "match_type",       label: "Match type",           category: "parameters", defaultVisible: false },
  { key: "active_cpp",       label: "Active CPP",           category: "parameters", defaultVisible: false },
  { key: "last_modified_at", label: "Last Date Modified",   category: "parameters", defaultVisible: false },
  { key: "modified_by",      label: "Modified By",          category: "parameters", defaultVisible: false },
  { key: "creation_time",    label: "Created Date",         category: "parameters", defaultVisible: false },
  { key: "rule_applied",     label: "Rule Applied",         category: "parameters", defaultVisible: false },
  { key: "rule_names",       label: "Rule Names",           category: "parameters", defaultVisible: false },
  { key: "rules",            label: "Rules",                category: "parameters", defaultVisible: false, comingSoon: true },
  { key: "tracked_installs", label: "Tracked installs",     category: "parameters", defaultVisible: false, comingSoon: true },
  { key: "qs_status",        label: "QS status",            category: "parameters", defaultVisible: false, comingSoon: true },
  { key: "goal_based_opt",   label: "Goal-based optimization", category: "parameters", defaultVisible: false, comingSoon: true },
  { key: "tags",             label: "Tags",                 category: "parameters", defaultVisible: false, comingSoon: true },
  // Ad-group ASA targeting parameters — live (sourced from
  // asa_adgroup_meta.targeting_*).
  { key: "search_match",     label: "Search match",         category: "parameters", defaultVisible: false },
  { key: "devices",          label: "Devices",              category: "parameters", defaultVisible: false },
  { key: "gender",           label: "Gender",               category: "parameters", defaultVisible: false },
  { key: "age",              label: "Age",                  category: "parameters", defaultVisible: false },
  { key: "locality",         label: "Locality",             category: "parameters", defaultVisible: false },
  { key: "admin_area",       label: "Admin area",           category: "parameters", defaultVisible: false },

  // Performance
  { key: "impressions",      label: "Impressions",          category: "performance", defaultVisible: true  },
  { key: "taps",             label: "Taps",                 category: "performance", defaultVisible: true  },
  { key: "ttr",              label: "TTR",                  category: "performance", defaultVisible: true  },
  { key: "avg_cpt",          label: "Average CPT",          category: "performance", defaultVisible: true  },
  { key: "spend",            label: "Spend",                category: "performance", defaultVisible: true  },
  { key: "installs",         label: "Installs",             category: "performance", defaultVisible: true  },
  { key: "installs_tap",     label: "Installs (Tap)",       category: "performance", defaultVisible: false },
  { key: "installs_view",    label: "Installs (View)",      category: "performance", defaultVisible: false },
  { key: "installs_total",   label: "Installs (Total)",     category: "performance", defaultVisible: false },
  { key: "cpa",              label: "Average CPA",          category: "performance", defaultVisible: true  },
  { key: "cpa_tap",          label: "Average CPA (Tap)",    category: "performance", defaultVisible: false },
  { key: "cpa_view",         label: "Average CPA (View)",   category: "performance", defaultVisible: false },
  { key: "cpa_total",        label: "Average CPA (Total)",  category: "performance", defaultVisible: false },
  { key: "cr_tap",           label: "CR (Tap)",             category: "performance", defaultVisible: false },
  { key: "cr_total",         label: "CR (Total)",           category: "performance", defaultVisible: false },
  { key: "new_downloads_tap",   label: "New downloads (Tap)",   category: "performance", defaultVisible: false },
  { key: "new_downloads_view",  label: "New downloads (View)",  category: "performance", defaultVisible: false },
  { key: "new_downloads_total", label: "New downloads (Total)", category: "performance", defaultVisible: false },
  { key: "redownloads_tap",   label: "Redownloads (Tap)",   category: "performance", defaultVisible: false },
  { key: "redownloads_view",  label: "Redownloads (View)",  category: "performance", defaultVisible: false },
  { key: "redownloads_total", label: "Redownloads (Total)", category: "performance", defaultVisible: false },
  // Share of voice by ad slot (Apple Ads API v1 Insights). The old key
  // `impression_share` stays as slot 1 so saved presets keep the column.
  { key: "impression_share",  label: "SOV slot 1",          category: "performance", defaultVisible: false },
  { key: "sov_slot2",         label: "SOV slot 2",          category: "performance", defaultVisible: false },
  { key: "sov_total",         label: "SOV total",           category: "performance", defaultVisible: false },
  { key: "search_rank",       label: "Search Rank",         category: "performance", defaultVisible: false },
  { key: "sov_2d",            label: "SOV 2d",              category: "performance", defaultVisible: false, comingSoon: true },
  { key: "sov_5d",            label: "SOV 5d",              category: "performance", defaultVisible: false, comingSoon: true },

  // Enriched data — IPM/CPM live now, the rest are v1.1 placeholders.
  { key: "ipm",              label: "IPM",                  category: "enriched", defaultVisible: false },
  { key: "cpm",              label: "CPM",                  category: "enriched", defaultVisible: false },
  { key: "active_capacity",  label: "Active capacity",      category: "enriched", defaultVisible: false, comingSoon: true },
  { key: "active_keywords",  label: "Active keywords",      category: "enriched", defaultVisible: false },
  { key: "active_share",     label: "Active share",         category: "enriched", defaultVisible: false, comingSoon: true },
  { key: "search_ads_popularity", label: "Search Popularity", category: "enriched", defaultVisible: false },
  { key: "organic_search",   label: "Organic Search",       category: "enriched", defaultVisible: false, comingSoon: true },
  { key: "organic_position", label: "Organic position",     category: "enriched", defaultVisible: false, comingSoon: true },

  // Conversions — full Adapty v2 webhook coverage (18 native events ×
  // {count, _cr, _revenue, _cpa}) + a single `revenue` aggregate.
  // All defaultVisible:false to keep the grid uncluttered; tab defaults
  // below opt a few in (trial_started + trial_started_cr +
  // subscription_started_revenue).
  { key: "revenue",                                     label: "Revenue",                                     category: "conversions", defaultVisible: false },
  { key: "trial_started",                               label: "trial_started",                               category: "conversions", defaultVisible: false },
  { key: "trial_started_cr",                            label: "trial_started_cr",                            category: "conversions", defaultVisible: false },
  { key: "trial_started_revenue",                       label: "trial_started_revenue",                       category: "conversions", defaultVisible: false },
  { key: "trial_started_cpa",                           label: "trial_started_cpa",                           category: "conversions", defaultVisible: false },
  { key: "trial_converted",                             label: "trial_converted",                             category: "conversions", defaultVisible: false },
  { key: "trial_converted_cr",                          label: "trial_converted_cr",                          category: "conversions", defaultVisible: false },
  { key: "trial_converted_revenue",                     label: "trial_converted_revenue",                     category: "conversions", defaultVisible: false },
  { key: "trial_converted_cpa",                         label: "trial_converted_cpa",                         category: "conversions", defaultVisible: false },
  { key: "trial_renewal_cancelled",                     label: "trial_renewal_cancelled",                     category: "conversions", defaultVisible: false },
  { key: "trial_renewal_cancelled_cr",                  label: "trial_renewal_cancelled_cr",                  category: "conversions", defaultVisible: false },
  { key: "trial_renewal_cancelled_revenue",             label: "trial_renewal_cancelled_revenue",             category: "conversions", defaultVisible: false },
  { key: "trial_renewal_cancelled_cpa",                 label: "trial_renewal_cancelled_cpa",                 category: "conversions", defaultVisible: false },
  { key: "trial_renewal_reactivated",                   label: "trial_renewal_reactivated",                   category: "conversions", defaultVisible: false },
  { key: "trial_renewal_reactivated_cr",                label: "trial_renewal_reactivated_cr",                category: "conversions", defaultVisible: false },
  { key: "trial_renewal_reactivated_revenue",           label: "trial_renewal_reactivated_revenue",           category: "conversions", defaultVisible: false },
  { key: "trial_renewal_reactivated_cpa",               label: "trial_renewal_reactivated_cpa",               category: "conversions", defaultVisible: false },
  { key: "trial_expired",                               label: "trial_expired",                               category: "conversions", defaultVisible: false },
  { key: "trial_expired_cr",                            label: "trial_expired_cr",                            category: "conversions", defaultVisible: false },
  { key: "trial_expired_revenue",                       label: "trial_expired_revenue",                       category: "conversions", defaultVisible: false },
  { key: "trial_expired_cpa",                           label: "trial_expired_cpa",                           category: "conversions", defaultVisible: false },
  { key: "subscription_started",                        label: "subscription_started",                        category: "conversions", defaultVisible: false },
  { key: "subscription_started_cr",                     label: "subscription_started_cr",                     category: "conversions", defaultVisible: false },
  { key: "subscription_started_revenue",                label: "subscription_started_revenue",                category: "conversions", defaultVisible: false },
  { key: "subscription_started_cpa",                    label: "subscription_started_cpa",                    category: "conversions", defaultVisible: false },
  { key: "subscription_renewed",                        label: "subscription_renewed",                        category: "conversions", defaultVisible: false },
  { key: "subscription_renewed_cr",                     label: "subscription_renewed_cr",                     category: "conversions", defaultVisible: false },
  { key: "subscription_renewed_revenue",                label: "subscription_renewed_revenue",                category: "conversions", defaultVisible: false },
  { key: "subscription_renewed_cpa",                    label: "subscription_renewed_cpa",                    category: "conversions", defaultVisible: false },
  { key: "subscription_renewal_cancelled",              label: "subscription_renewal_cancelled",              category: "conversions", defaultVisible: false },
  { key: "subscription_renewal_cancelled_cr",           label: "subscription_renewal_cancelled_cr",           category: "conversions", defaultVisible: false },
  { key: "subscription_renewal_cancelled_revenue",      label: "subscription_renewal_cancelled_revenue",      category: "conversions", defaultVisible: false },
  { key: "subscription_renewal_cancelled_cpa",          label: "subscription_renewal_cancelled_cpa",          category: "conversions", defaultVisible: false },
  { key: "subscription_renewal_reactivated",            label: "subscription_renewal_reactivated",            category: "conversions", defaultVisible: false },
  { key: "subscription_renewal_reactivated_cr",         label: "subscription_renewal_reactivated_cr",         category: "conversions", defaultVisible: false },
  { key: "subscription_renewal_reactivated_revenue",    label: "subscription_renewal_reactivated_revenue",    category: "conversions", defaultVisible: false },
  { key: "subscription_renewal_reactivated_cpa",        label: "subscription_renewal_reactivated_cpa",        category: "conversions", defaultVisible: false },
  { key: "subscription_expired",                        label: "subscription_expired",                        category: "conversions", defaultVisible: false },
  { key: "subscription_expired_cr",                     label: "subscription_expired_cr",                     category: "conversions", defaultVisible: false },
  { key: "subscription_expired_revenue",                label: "subscription_expired_revenue",                category: "conversions", defaultVisible: false },
  { key: "subscription_expired_cpa",                    label: "subscription_expired_cpa",                    category: "conversions", defaultVisible: false },
  { key: "subscription_paused",                         label: "subscription_paused",                         category: "conversions", defaultVisible: false },
  { key: "subscription_paused_cr",                      label: "subscription_paused_cr",                      category: "conversions", defaultVisible: false },
  { key: "subscription_paused_revenue",                 label: "subscription_paused_revenue",                 category: "conversions", defaultVisible: false },
  { key: "subscription_paused_cpa",                     label: "subscription_paused_cpa",                     category: "conversions", defaultVisible: false },
  { key: "subscription_deferred",                       label: "subscription_deferred",                       category: "conversions", defaultVisible: false },
  { key: "subscription_deferred_cr",                    label: "subscription_deferred_cr",                    category: "conversions", defaultVisible: false },
  { key: "subscription_deferred_revenue",               label: "subscription_deferred_revenue",               category: "conversions", defaultVisible: false },
  { key: "subscription_deferred_cpa",                   label: "subscription_deferred_cpa",                   category: "conversions", defaultVisible: false },
  { key: "subscription_refunded",                       label: "subscription_refunded",                       category: "conversions", defaultVisible: false },
  { key: "subscription_refunded_cr",                    label: "subscription_refunded_cr",                    category: "conversions", defaultVisible: false },
  { key: "subscription_refunded_revenue",               label: "subscription_refunded_revenue",               category: "conversions", defaultVisible: false },
  { key: "subscription_refunded_cpa",                   label: "subscription_refunded_cpa",                   category: "conversions", defaultVisible: false },
  { key: "non_subscription_purchase",                   label: "non_subscription_purchase",                   category: "conversions", defaultVisible: false },
  { key: "non_subscription_purchase_cr",                label: "non_subscription_purchase_cr",                category: "conversions", defaultVisible: false },
  { key: "non_subscription_purchase_revenue",           label: "non_subscription_purchase_revenue",           category: "conversions", defaultVisible: false },
  { key: "non_subscription_purchase_cpa",               label: "non_subscription_purchase_cpa",               category: "conversions", defaultVisible: false },
  { key: "non_subscription_purchase_refunded",          label: "non_subscription_purchase_refunded",          category: "conversions", defaultVisible: false },
  { key: "non_subscription_purchase_refunded_cr",       label: "non_subscription_purchase_refunded_cr",       category: "conversions", defaultVisible: false },
  { key: "non_subscription_purchase_refunded_revenue",  label: "non_subscription_purchase_refunded_revenue",  category: "conversions", defaultVisible: false },
  { key: "non_subscription_purchase_refunded_cpa",      label: "non_subscription_purchase_refunded_cpa",      category: "conversions", defaultVisible: false },
  { key: "entered_grace_period",                        label: "entered_grace_period",                        category: "conversions", defaultVisible: false },
  { key: "entered_grace_period_cr",                     label: "entered_grace_period_cr",                     category: "conversions", defaultVisible: false },
  { key: "entered_grace_period_revenue",                label: "entered_grace_period_revenue",                category: "conversions", defaultVisible: false },
  { key: "entered_grace_period_cpa",                    label: "entered_grace_period_cpa",                    category: "conversions", defaultVisible: false },
  { key: "billing_issue_detected",                      label: "billing_issue_detected",                      category: "conversions", defaultVisible: false },
  { key: "billing_issue_detected_cr",                   label: "billing_issue_detected_cr",                   category: "conversions", defaultVisible: false },
  { key: "billing_issue_detected_revenue",              label: "billing_issue_detected_revenue",              category: "conversions", defaultVisible: false },
  { key: "billing_issue_detected_cpa",                  label: "billing_issue_detected_cpa",                  category: "conversions", defaultVisible: false },
  { key: "access_level_updated",                        label: "access_level_updated",                        category: "conversions", defaultVisible: false },
  { key: "access_level_updated_cr",                     label: "access_level_updated_cr",                     category: "conversions", defaultVisible: false },
  { key: "access_level_updated_revenue",                label: "access_level_updated_revenue",                category: "conversions", defaultVisible: false },
  { key: "access_level_updated_cpa",                    label: "access_level_updated_cpa",                    category: "conversions", defaultVisible: false },

  // Adjust per-event breakdown (source='adjust' apps only; read from row.adjust)
  { key: "adj_mobilepurchase", label: "MobilePurchase", category: "conversions", defaultVisible: false },
  { key: "adj_mobilepurchase_cr", label: "MobilePurchase CR", category: "conversions", defaultVisible: false },
  { key: "adj_mobilepurchase_cpa", label: "MobilePurchase CPA", category: "conversions", defaultVisible: false },
  { key: "adj_mobilepurchase_revenue", label: "MobilePurchase Revenue", category: "conversions", defaultVisible: false },
  { key: "adj_rv_nonsubscriptionpurchase", label: "rv_NonSubscriptionPurchase", category: "conversions", defaultVisible: false },
  { key: "adj_rv_nonsubscriptionpurchase_cr", label: "rv_NonSubscriptionPurchase CR", category: "conversions", defaultVisible: false },
  { key: "adj_rv_nonsubscriptionpurchase_cpa", label: "rv_NonSubscriptionPurchase CPA", category: "conversions", defaultVisible: false },
  { key: "adj_rv_nonsubscriptionpurchase_revenue", label: "rv_NonSubscriptionPurchase Revenue", category: "conversions", defaultVisible: false },
  { key: "adj_rv_productchange", label: "rv_ProductChange", category: "conversions", defaultVisible: false },
  { key: "adj_rv_productchange_cr", label: "rv_ProductChange CR", category: "conversions", defaultVisible: false },
  { key: "adj_rv_productchange_cpa", label: "rv_ProductChange CPA", category: "conversions", defaultVisible: false },
  { key: "adj_rv_subscriptioncancel", label: "rv_SubscriptionCancel", category: "conversions", defaultVisible: false },
  { key: "adj_rv_subscriptioncancel_cr", label: "rv_SubscriptionCancel CR", category: "conversions", defaultVisible: false },
  { key: "adj_rv_subscriptioncancel_cpa", label: "rv_SubscriptionCancel CPA", category: "conversions", defaultVisible: false },
  { key: "adj_rv_subscriptionexpiration", label: "rv_SubscriptionExpiration", category: "conversions", defaultVisible: false },
  { key: "adj_rv_subscriptionexpiration_cr", label: "rv_SubscriptionExpiration CR", category: "conversions", defaultVisible: false },
  { key: "adj_rv_subscriptionexpiration_cpa", label: "rv_SubscriptionExpiration CPA", category: "conversions", defaultVisible: false },
  { key: "adj_rv_subscriptioninitialpurchase", label: "rv_SubscriptionInitialPurchase", category: "conversions", defaultVisible: false },
  { key: "adj_rv_subscriptioninitialpurchase_cr", label: "rv_SubscriptionInitialPurchase CR", category: "conversions", defaultVisible: false },
  { key: "adj_rv_subscriptioninitialpurchase_cpa", label: "rv_SubscriptionInitialPurchase CPA", category: "conversions", defaultVisible: false },
  { key: "adj_rv_subscriptioninitialpurchase_revenue", label: "rv_SubscriptionInitialPurchase Revenue", category: "conversions", defaultVisible: false },
  { key: "adj_rv_subscriptionrenewal", label: "rv_SubscriptionRenewal", category: "conversions", defaultVisible: false },
  { key: "adj_rv_subscriptionrenewal_cr", label: "rv_SubscriptionRenewal CR", category: "conversions", defaultVisible: false },
  { key: "adj_rv_subscriptionrenewal_cpa", label: "rv_SubscriptionRenewal CPA", category: "conversions", defaultVisible: false },
  { key: "adj_rv_subscriptionrenewal_revenue", label: "rv_SubscriptionRenewal Revenue", category: "conversions", defaultVisible: false },
  { key: "adj_rv_subscriptiontrialactivate", label: "rv_SubscriptionTrialActivate", category: "conversions", defaultVisible: false },
  { key: "adj_rv_subscriptiontrialactivate_cr", label: "rv_SubscriptionTrialActivate CR", category: "conversions", defaultVisible: false },
  { key: "adj_rv_subscriptiontrialactivate_cpa", label: "rv_SubscriptionTrialActivate CPA", category: "conversions", defaultVisible: false },
  { key: "adj_rv_subscriptiontrialactivate_revenue", label: "rv_SubscriptionTrialActivate Revenue", category: "conversions", defaultVisible: false },
  { key: "adj_rv_subscriptiontrialcancel", label: "rv_SubscriptionTrialCancel", category: "conversions", defaultVisible: false },
  { key: "adj_rv_subscriptiontrialcancel_cr", label: "rv_SubscriptionTrialCancel CR", category: "conversions", defaultVisible: false },
  { key: "adj_rv_subscriptiontrialcancel_cpa", label: "rv_SubscriptionTrialCancel CPA", category: "conversions", defaultVisible: false },
  { key: "adj_rv_subscriptiontrialstart", label: "rv_SubscriptionTrialStart", category: "conversions", defaultVisible: false },
  { key: "adj_rv_subscriptiontrialstart_cr", label: "rv_SubscriptionTrialStart CR", category: "conversions", defaultVisible: false },
  { key: "adj_rv_subscriptiontrialstart_cpa", label: "rv_SubscriptionTrialStart CPA", category: "conversions", defaultVisible: false },

  // Cohort (ROAS / ROI / ARPU across D1, D4, D7, D10, D20, D30, D35)
  { key: "roas",             label: "ROAS",                 category: "cohort", defaultVisible: false },
  { key: "roas_d1",          label: "ROAS D1",              category: "cohort", defaultVisible: false },
  { key: "roas_d4",          label: "ROAS D4",              category: "cohort", defaultVisible: true  },
  { key: "roas_d7",          label: "ROAS D7",              category: "cohort", defaultVisible: false },
  { key: "roas_d10",         label: "ROAS D10",             category: "cohort", defaultVisible: false },
  { key: "roas_d20",         label: "ROAS D20",             category: "cohort", defaultVisible: true  },
  { key: "roas_d30",         label: "ROAS D30",             category: "cohort", defaultVisible: false },
  { key: "roas_d35",         label: "ROAS D35",             category: "cohort", defaultVisible: false },
  { key: "roi_d1",           label: "ROI D1",               category: "cohort", defaultVisible: false },
  { key: "roi_d4",           label: "ROI D4",               category: "cohort", defaultVisible: true  },
  { key: "roi_d7",           label: "ROI D7",               category: "cohort", defaultVisible: false },
  { key: "roi_d10",          label: "ROI D10",              category: "cohort", defaultVisible: false },
  { key: "roi_d20",          label: "ROI D20",              category: "cohort", defaultVisible: true  },
  { key: "roi_d30",          label: "ROI D30",              category: "cohort", defaultVisible: false },
  { key: "roi_d35",          label: "ROI D35",              category: "cohort", defaultVisible: false },
  { key: "arpu_d1",          label: "ARPU D1",              category: "cohort", defaultVisible: false },
  { key: "arpu_d4",          label: "ARPU D4",              category: "cohort", defaultVisible: false },
  { key: "arpu_d7",          label: "ARPU D7",              category: "cohort", defaultVisible: false },
  { key: "arpu_d10",         label: "ARPU D10",             category: "cohort", defaultVisible: false },
  { key: "arpu_d20",         label: "ARPU D20",             category: "cohort", defaultVisible: false },
  { key: "arpu_d30",         label: "ARPU D30",             category: "cohort", defaultVisible: false },
  { key: "arpu_d35",         label: "ARPU D35",             category: "cohort", defaultVisible: false },
];

export const CATEGORY_ORDER: ColumnCategory[] = [
  "performance",
  "enriched",
  "cohort",
  "parameters",
  "conversions",
];

export const CATEGORY_LABEL: Record<ColumnCategory, string> = {
  performance: "Performance",
  enriched: "Enriched data",
  cohort: "Cohort",
  parameters: "Parameters",
  conversions: "Conversions",
};

export function storageKeyFor(tab: string): string {
  // Bump version when defaults change meaningfully — old localStorage
  // entries otherwise stick around and users don't see new defaults.
  // v3 (2026-04-29): legacy aggregates trials/trial_cvr/paid_cvr/revenue
  // were retired.
  // v4 (2026-04-30): per-tab parameter / performance / enriched
  // allowlists narrowed to ASA's level-specific fields.
  // v5 (2026-04-30 pm): the localStorage payload changed shape from a
  // visibility-only ColumnKey[] (de-facto Set) to an ORDERED array
  // honouring user drag-reorder. Pinned columns (status / daily_budget
  // / etc.) get auto-prepended on read, so old v4 lists which omit
  // them won't resurface as "missing".
  // v6 (2026-04-30 evening): retired `org_id` + `spend_forecast` from
  // the catalogue; old saved sets with those keys would get filtered
  // to nothing-applicable on load, so a fresh key avoids ghosts.
  // v7 (2026-05-12): `last_modified` ColumnKey renamed to
  // `last_modified_at` (semantics changed from collector's updated_at
  // → real platform-driven edit stamp from migration 0062). New
  // `modified_by` column joins it. Bump avoids stale v6 lists with
  // dangling `last_modified` references getting filtered to nothing.
  // v8 (2026-05-13): adds `rule_applied` + `rule_names` opt-in
  // columns surfacing rule attachments per entity. Old v7 lists
  // simply won't include them (additive), so the bump is technically
  // not required — kept for symmetry with prior column additions.
  // v10 (2026-07-31): adds `translation_en`, which ships ON by default.
  // Additive keys normally need no bump, but a default-visible one does:
  // without it every existing user keeps a saved v9 list that predates the
  // column and nobody ever sees it.
  return `oneads.ads_manager.columns.v10.${tab}`;
}

/**
 * Pinned columns per tab — always visible AND always at the start of
 * the table, in this exact order. Cannot be unchecked, cannot be
 * dragged. The grain's identity column (campaign_name / ad_group_name
 * / keyword text) is rendered separately by StatsTable as the first
 * cell — these pins live AFTER the identity, so the visual order is:
 *   identity → pinned[0] → pinned[1] → ... → user-ordered rest.
 */
export function pinnedKeysForTab(tab: string): ColumnKey[] {
  switch (tab) {
    case "campaigns":
      // Per user 2026-04-30: campaign_name (identity) + daily_budget +
      // status are the locked first three.
      return ["daily_budget", "status"];
    case "ad_groups":
      // ad_group_name (identity) + status.
      return ["status"];
    case "keywords":
      // keyword (identity) + status.
      return ["status"];
    default:
      return [];
  }
}

/**
 * Per-tab whitelist for `parameters`, `performance`, and `enriched`
 * categories. Conversions and Cohort are always included — they apply
 * to every grain. Apple Search Ads structures fields at specific
 * levels (e.g. `default_bid` is ad-group-only, `bid` is keyword-only,
 * `target_cpa` is campaign-only) so showing the wrong field on the
 * wrong tab rendered "—" forever and confused users into thinking
 * data was missing. This map also drives v1.1 columns (Impression
 * Share, SOV 2d/5d, Devices, Gender, etc.) that exist as picker
 * entries but render placeholders until the underlying data is wired.
 */
const _ALLOWED_BY_TAB: Record<
  string,
  { parameters: ColumnKey[]; performance: ColumnKey[]; enriched: ColumnKey[] }
> = {
  campaigns: {
    parameters: [
      "status",
      "label",
      "campaign_id",
      "daily_budget",
      "placement",
      "country",
      "target_cpa",
      "last_modified_at",
      "creation_time",
      "modified_by",
      "rule_applied",
      "rule_names",
      "tags",
    ],
    // Per user 2026-05-15: dropped the legacy `installs` (= installs_asa)
    // and `cpa` (= spend/installs_asa) columns. They show different
    // numbers from `installs_total` / `cpa_total` ONLY because the
    // tap/view/total breakdown was added 2026-04-29 and historical
    // rows had breakdown=0 even though installs_asa was populated.
    // The rules engine ONLY reads tap/view/total (see
    // `app/services/rules/engine.py:192-199`); rules at any level
    // never match against installs_asa. Keeping these two on the
    // Campaigns tab created a false impression that they were
    // independently meaningful — they aren't. Ad Group + Keyword
    // tabs already didn't expose them. Now Campaign aligns.
    performance: [
      "impressions",
      "taps",
      "ttr",
      "avg_cpt",
      "spend",
      "installs_tap",
      "installs_view",
      "installs_total",
      "cr_tap",
      "cr_total",
      "cpa_tap",
      "cpa_view",
      "cpa_total",
      "new_downloads_tap",
      "new_downloads_view",
      "new_downloads_total",
      "redownloads_tap",
      "redownloads_view",
      "redownloads_total",
      "sov_2d",
      "sov_5d",
    ],
    enriched: ["ipm", "cpm", "active_capacity", "active_keywords", "active_share", "tags"],
  },
  ad_groups: {
    // Per ASA: ad-group-level parameters. Devices / Gender / Age /
    // Locality / Admin area / Search match are ASA targeting knobs;
    // shown in the picker even though not yet populated server-side.
    parameters: [
      "status",
      "label",
      "ad_group_id",
      "default_bid",
      "cpa_goal",
      "search_match",
      "last_modified_at",
      "creation_time",
      "modified_by",
      "rule_applied",
      "rule_names",
      "devices",
      "gender",
      "age",
      "locality",
      "admin_area",
      "active_cpp",
    ],
    performance: [
      "impressions",
      "taps",
      "ttr",
      "avg_cpt",
      "spend",
      "installs_tap",
      "installs_view",
      "installs_total",
      "cr_tap",
      "cr_total",
      "cpa_tap",
      "cpa_view",
      "cpa_total",
      "new_downloads_tap",
      "new_downloads_view",
      "new_downloads_total",
      "redownloads_tap",
      "redownloads_view",
      "redownloads_total",
      "sov_2d",
      "sov_5d",
      "impression_share",
      "sov_slot2",
      "sov_total",
    ],
    enriched: ["ipm", "cpm", "tags"],
  },
  keywords: {
    parameters: [
      // Keyword grain only. Campaign and ad-group names are the advertiser's
      // own and never need translating.
      "translation_en",
      "status",
      "match_type",
      "keyword_id",
      "bid",
      "last_modified_at",
      "creation_time",
      "modified_by",
      "rule_applied",
      "rule_names",
    ],
    performance: [
      "impressions",
      "taps",
      "ttr",
      "avg_cpt",
      "spend",
      "installs_tap",
      "installs_view",
      "installs_total",
      "cr_tap",
      "cr_total",
      "cpa_tap",
      "cpa_view",
      "cpa_total",
      "new_downloads_tap",
      "new_downloads_view",
      "new_downloads_total",
      "redownloads_tap",
      "redownloads_view",
      "redownloads_total",
      "impression_share",
      "sov_slot2",
      "sov_total",
      "search_rank",
      "sov_2d",
      "sov_5d",
    ],
    enriched: [
      "search_ads_popularity",
      "organic_search",
      "organic_position",
      "ipm",
      "cpm",
    ],
  },
};

export type CohortSource = "adapty" | "adjust";

// Adjust per-event column keys (the 35 `adj_*` keys).
export const ADJUST_COLUMN_KEYS: ColumnKey[] = ALL_COLUMNS.filter(
  (c) => c.key.startsWith("adj_"),
).map((c) => c.key);
const _ADJUST_SET = new Set<ColumnKey>(ADJUST_COLUMN_KEYS);

// The 18 Adapty per-event columns (conversions minus the adj_* keys
// and the aggregate `revenue`). Hidden for Adjust apps and REPLACED by
// the 10 Adjust events. The aggregate `revenue` + cohort columns
// (ROAS/Revenue/ROI/ARPU × D) are KEPT — the backend feeds them from
// Adjust cohort revenue for source='adjust' apps, so they show real
// Adjust numbers (standard Revenue + ROAS-any-day), exactly like every
// other project.
const _ADAPTY_EVENT_SET = new Set<ColumnKey>(
  ALL_COLUMNS.filter(
    (c) =>
      c.category === "conversions" &&
      !c.key.startsWith("adj_") &&
      c.key !== "revenue",
  ).map((c) => c.key),
);

/**
 * Swap the per-event column set to match the app's data source. Adjust
 * apps hide the 18 Adapty events (replaced by the 10 Adjust events) but
 * KEEP the aggregate Revenue + cohort ROAS/ARPU columns, which the
 * backend feeds from Adjust. Everyone else hides the Adjust events.
 */
function _filterByCohortSource(
  keys: ColumnKey[],
  cohortSource?: CohortSource,
): ColumnKey[] {
  const drop = cohortSource === "adjust" ? _ADAPTY_EVENT_SET : _ADJUST_SET;
  return keys.filter((k) => !drop.has(k));
}

export function allowedColumnsForTab(
  tab: string,
  cohortSource?: CohortSource,
): ColumnKey[] {
  const allow = _ALLOWED_BY_TAB[tab];
  if (!allow) {
    // Unknown tab — fall back to "everything" so we don't accidentally
    // hide all columns for a future grain that hasn't been added here.
    return _filterByCohortSource(
      ALL_COLUMNS.map((c) => c.key),
      cohortSource,
    );
  }
  const allowedSet = new Set<ColumnKey>([
    ...allow.parameters,
    ...allow.performance,
    ...allow.enriched,
  ]);
  const base = ALL_COLUMNS.filter(
    (c) =>
      // Conversions + cohort always pass through — these are
      // grain-agnostic metric breakdowns.
      c.category === "conversions" ||
      c.category === "cohort" ||
      allowedSet.has(c.key)
  ).map((c) => c.key);
  return _filterByCohortSource(base, cohortSource);
}

/**
 * Default visible columns per tab. Returned as an ordered array so
 * drag-reorder + pinning work consistently with localStorage state.
 * Pinned columns are auto-prepended; callers don't have to remember.
 */
export function defaultVisibleColumnsForTab(tab: string): ColumnKey[] {
  return _withPinnedFirst(tab, _defaultBodyColumnsForTab(tab));
}

function _withPinnedFirst(tab: string, body: ColumnKey[]): ColumnKey[] {
  const pinned = pinnedKeysForTab(tab);
  // Pinned first (in fixed order), then body minus any pinned dupes.
  const seen = new Set<ColumnKey>(pinned);
  const out: ColumnKey[] = [...pinned];
  for (const k of body) {
    if (!seen.has(k)) {
      out.push(k);
      seen.add(k);
    }
  }
  return out;
}

function _defaultBodyColumnsSet(tab: string): Set<ColumnKey> {
  switch (tab) {
    case "campaigns":
      return new Set<ColumnKey>([
        "status",
        "daily_budget",
        "impressions",
        "taps",
        "ttr",
        "avg_cpt",
        "spend",
        "installs",
        "cpa",
        "trial_started",
        "trial_started_cr",
        "subscription_started_revenue",
        "roas_d4",
      ]);
    case "ad_groups":
      // Per ASA's level conventions: ad-group performance is sliced
      // by attribution source (tap vs view vs total). The overall
      // `installs` / `cpa` columns are intentionally NOT in the picker
      // for this tab — pick `installs_total` + `cpa_total` for the
      // closest equivalent.
      return new Set<ColumnKey>([
        "status",
        "default_bid",
        "cpa_goal",
        "impressions",
        "taps",
        "ttr",
        "spend",
        "installs_total",
        "cpa_total",
        "trial_started",
        "roas_d4",
      ]);
    case "keywords":
      // Same rule as ad_groups: keyword performance uses the split
      // attribution columns. `bid` is now labelled "CPT bid".
      return new Set<ColumnKey>([
        // On by default: the cache is populated at ingest precisely because the
        // column is on for everyone (§10/C1). A user who does not want it turns
        // it off in the column picker.
        "translation_en",
        "status",
        "bid",
        "match_type",
        "impressions",
        "taps",
        "ttr",
        "spend",
        "installs_total",
        "cpa_total",
        "trial_started",
        "roas_d4",
      ]);
    case "negative_keywords":
      return new Set<ColumnKey>(["status"]);
    case "search_terms":
      return new Set<ColumnKey>([
        "impressions",
        "taps",
        "installs",
        "spend",
      ]);
    case "ads":
      return new Set<ColumnKey>(["status"]);
    default:
      return new Set(
        ALL_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key)
      );
  }
}

function _defaultBodyColumnsForTab(tab: string): ColumnKey[] {
  // Body = everything visible by default minus the pinned set; the
  // wrapper above prepends pinned in fixed order.
  const set = _defaultBodyColumnsSet(tab);
  const pinned = new Set(pinnedKeysForTab(tab));
  // Walk ALL_COLUMNS so the "default body" order matches the catalogue
  // grouping (parameters / performance / enriched / conversions /
  // cohort) — pre-drag-reorder, this is what users saw.
  return ALL_COLUMNS.filter(
    (c) => set.has(c.key) && !pinned.has(c.key)
  ).map((c) => c.key);
}

/**
 * Load the user's saved column ordering for `tab`. Always returns
 * pinned keys first (in fixed order), then the user's chosen body.
 * Unknown keys (retired ColumnKey, schema drift) are filtered out.
 */
export function loadVisibleColumns(tab: string = "campaigns"): ColumnKey[] {
  const keys = readPref<string[] | null>(storageKeyFor(tab), null);
  if (!keys) return defaultVisibleColumnsForTab(tab);
  const known = keys.filter((k): k is ColumnKey =>
    ALL_COLUMNS.some((c) => c.key === k)
  );
  return _withPinnedFirst(tab, known);
}

/** Persist the user's ordered column list for `tab`. Pinned keys are
 *  forced to the front in their fixed order regardless of what `cols`
 *  contains, so drag-reorder can never strand a pin. */
export function saveVisibleColumns(tab: string, cols: ColumnKey[]): void {
  writePref(storageKeyFor(tab), _withPinnedFirst(tab, cols));
}

/** Resolve a preset's raw `columns` array into the actual visible-cols
 *  list a StatsTable consumer would set on state: filtered by the
 *  tab's allowed-keys whitelist, with pinned keys force-prepended in
 *  fixed order. Shared by ColumnsButton's apply-preset path AND the
 *  page-mount auto-apply-default-preset path so both paths produce
 *  the exact same column order. */
export function resolvePresetColumns(
  tab: string,
  presetColumns: readonly string[],
  allowedKeys?: ColumnKey[],
): ColumnKey[] {
  const allow = allowedKeys ? new Set(allowedKeys) : null;
  const keys = (presetColumns as ColumnKey[]).filter(
    (k) => !allow || allow.has(k),
  );
  return _withPinnedFirst(tab, keys);
}


// ---------------------------------------------------------------------------
// Column WIDTHS — orthogonal preference to visible/order. Stored in a
// separate localStorage key so the two move independently:
//   - changing visibility / order doesn't reset widths
//   - resizing a column doesn't touch the order list
//
// Shape: { [columnKey]: pixels:int }. Only customized columns appear;
// missing keys mean "default width" (StatsTable decides).
// ---------------------------------------------------------------------------

export function widthsStorageKeyFor(tab: string): string {
  // v1 — shipped 2026-05-12 alongside the manual-resize feature.
  // Bump if the shape ever changes (e.g. add per-preset widths here).
  return `oneads.ads_manager.widths.v1.${tab}`;
}

export function loadColumnWidths(tab: string): Record<string, number> {
  const raw = readPref<Record<string, number> | null>(
    widthsStorageKeyFor(tab),
    null,
  );
  if (!raw || typeof raw !== "object") return {};
  // Sanity-filter: drop non-numeric / sub-min values so a corrupt blob
  // doesn't collapse a column to invisible.
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (typeof v === "number" && v >= MIN_COLUMN_WIDTH) out[k] = v;
  }
  return out;
}

export function saveColumnWidths(
  tab: string,
  widths: Record<string, number>,
): void {
  writePref(widthsStorageKeyFor(tab), widths);
}

// Minimum pixel width per column — keeps the user from accidentally
// dragging a column down to 0 and losing it visually.
export const MIN_COLUMN_WIDTH = 40;
